import { useEffect, useRef } from "react";
import { useTheme } from "../../hooks/useTheme";

const TRAIL_COLORS = [
    [216, 180, 254],   // lavender-300
    [167, 139, 250],   // violet-400
    [192, 132, 252],   // purple-400
    [34,  211, 238],   // cyan-400
    [125, 211, 252],   // sky-300
    [255, 255, 255],   // white
    [139,  92, 246],   // violet-500
];

/**
 * Full-page cursor particle trail — light theme only.
 *
 * Mouse move → trailing glowing dots that drift upward & fade.
 */
export default function CursorTrail() {
    const { theme } = useTheme();
    const canvasRef = useRef(null);

    useEffect(() => {
        if (theme !== "light") return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");

        let raf;
        let lastSpawnX = -999, lastSpawnY = -999;

        const particles = [];  // trail dots only

        // ── Resize ───────────────────────────────────────────────
        const resize = () => {
            canvas.width  = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener("resize", resize);

        // ── Trail spawner (mouse move) ────────────────────────────
        function spawnTrail(x, y) {
            const n = 3 + Math.floor(Math.random() * 3);
            for (let i = 0; i < n; i++) {
                const [r, g, b] = TRAIL_COLORS[Math.floor(Math.random() * TRAIL_COLORS.length)];
                particles.push({
                    x:     x + (Math.random() - 0.5) * 10,
                    y:     y + (Math.random() - 0.5) * 10,
                    vx:    (Math.random() - 0.5) * 1.5,
                    vy:    -0.5 - Math.random() * 1.8,
                    size:  1.2 + Math.random() * 3.8,
                    life:  1.0,
                    decay: 0.013 + Math.random() * 0.022,
                    r, g, b,
                });
            }
        }

        // ── Event listeners ───────────────────────────────────────
        const onMouseMove = (e) => {
            const x = e.clientX, y = e.clientY;
            const dx = x - lastSpawnX, dy = y - lastSpawnY;
            if (Math.sqrt(dx * dx + dy * dy) > 7) {
                spawnTrail(x, y);
                lastSpawnX = x;
                lastSpawnY = y;
            }
        };

        window.addEventListener("mousemove", onMouseMove);

        // ── Draw loop ─────────────────────────────────────────────
        function draw() {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Particles (trail only)
            for (let i = particles.length - 1; i >= 0; i--) {
                const p = particles[i];
                p.life -= p.decay;
                if (p.life <= 0) { particles.splice(i, 1); continue; }

                p.x  += p.vx;
                p.y  += p.vy;
                p.vy *= 0.97;

                const a = p.life;

                // Glow halo
                const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3);
                grd.addColorStop(0, `rgba(${p.r},${p.g},${p.b},${a * 0.55})`);
                grd.addColorStop(1, `rgba(${p.r},${p.g},${p.b},0)`);
                ctx.fillStyle = grd;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
                ctx.fill();

                // Solid core
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${p.r},${p.g},${p.b},${a * 0.92})`;
                ctx.fill();
            }

            raf = requestAnimationFrame(draw);
        }
        draw();

        // ── Cleanup ───────────────────────────────────────────────
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize",    resize);
            window.removeEventListener("mousemove", onMouseMove);
        };
    }, [theme]);

    if (theme !== "light") return null;

    return (
        <canvas
            ref={canvasRef}
            aria-hidden
            style={{
                position:      "fixed",
                inset:         0,
                pointerEvents: "none",
                zIndex:        3,
            }}
        />
    );
}
